/**
 * The agent wallet's numbers (spec docs/superpowers/specs/2026-10-15-agent-wallet-design.md). One copy, read by the
 * server's enforcers, the runner and the Settings field, so the bounds a person reads are the bounds the server keeps.
 */

/** The currencies a budget, and a shop, may use. Any other shop currency is refused. */
export const AGENT_BUDGET_CURRENCIES = ['INR', 'USD'] as const;
export type AgentBudgetCurrency = (typeof AGENT_BUDGET_CURRENCIES)[number];

/** The smallest monthly budget, in minor units: ₹10, $1. */
export const AGENT_BUDGET_MIN_MINOR: Record<AgentBudgetCurrency, number> = { INR: 1_000, USD: 100 };

/**
 * The largest monthly budget, in minor units: ₹1,00,000, $1,000. The founder approved these on 2026-10-15. That
 * makes the agent budget the one documented exception to the $100 ceiling on every other settable limit
 * (constants/defaults.ts): this budget pays for purchases such as flights, not only for model spend.
 */
export const AGENT_BUDGET_MAX_MINOR: Record<AgentBudgetCurrency, number> = { INR: 10_000_000, USD: 100_000 };

/** What one credit is worth in a budget's minor units: ₹1 a credit (billing/prices.ts), and 100 credits = $1. */
export const MINOR_PER_CREDIT: Record<AgentBudgetCurrency, number> = { INR: 100, USD: 1 };

/** The founder-set rate for a purchase in the other currency. No live rate, on purpose (billing/prices.ts). */
export const PURCHASE_FX_INR_PER_USD = 88;

/** The extra a cross-currency HOLD takes, so a small rate move never pushes a paid amount past its hold. */
export const PURCHASE_FX_HOLD_BUFFER = 1.03;

/** How long a purchase waits for the person's answer before it expires. */
export const PURCHASE_APPROVAL_TTL_MS = 30 * 60_000;

/** How long the pay step may stay open before the server ends it. */
export const PAY_STEP_TTL_MS = 30 * 60_000;

/** How long after the pay step the agent has to report what the shop's confirmation said. */
export const PURCHASE_REPORT_TTL_MS = 15 * 60_000;

/** The runner's own expiry on a freeze it never hears the end of. Longer than the pay step, so it is only a backstop. */
export const PAYMENT_FREEZE_MAX_MS = 35 * 60_000;

/** The allowance of the one turn that carries a purchase's result, so a purchase that used up the month is still reported. */
export const PURCHASE_REPORT_TURN_USD = 0.25;

/** How the API names Bash, the lead, whose agent_id is NULL everywhere. */
export const LEAD_AGENT_KEY = 'bash';

/** The id of Settings' Agent budgets section (AgentBudgetsSettings), where every "Raise the budget" door lands. */
export const AGENT_BUDGETS_ANCHOR = 'agent-budgets';

/**
 * The ErrorBlock `code` (and outcome raw subtype) of the server's refusal of a used-up agent month
 * (gate-refusal.ts refuseForAgentMonth). One spelling for the server and the web, so a card tells the server's named
 * refusal from an engine trip by this field, never by its words.
 */
export const AGENT_MONTH_SPENT_CODE = 'agent_month_spent';

/**
 * How long after a pay step ends nothing may picture the box's screen: the web's saved frames and the server's
 * screenshot alike (P3 task 13). The runner covers and closes the payment page only after the route answers, so its
 * last frames may still show it.
 */
export const PAY_STEP_SCREEN_GRACE_MS = 15_000;
